import "../styles/login.scss";
import { useEffect, useState } from "react";
import { dispatch, routerHistory } from "../redux/redux-config";
import actions from "../actions/actions";
import { useSelector } from "react-redux";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { isInstalled, useUpdateState } from "../utilities/utilities";
import { Spinner } from "../components/loading-spinner";
import { ToastContainer } from "../components/toast";
import { GuiButton } from "../components/global-ui/gui-button.jsx";
import CircularProgress from "../actions/CircularProgress.js";
import { MobileDownloadBanner } from "../components/mobile-download-banner";
import ForgotPassword from "./ForgotPassword";
import VerificationCode from "./VerificationCode";
import ResetPassword from "./ResetPassword";
import LinkExpired from "./LinkExpired";
import PasswordCreated from "./PasswordCreated";

export default function LoginPage() {
  const [isSSOLogin, setSSOLogin] = useState(true);
  let switchLoginScreen = useSelector((store) => store.user.switchLoginScreen);

  useEffect(() => {
    if (
      window.location.pathname === "/" &&
      window.location.search.startsWith("?jwt=")
    ) {
      // Direct HGS JWT from admin portal impersonation — store and log in immediately
      const queryParams = new URLSearchParams(routerHistory.location.search);
      const hgsJwt = queryParams.get("jwt");
      if (hgsJwt) {
        sessionStorage.setItem("session", hgsJwt);
        dispatch("LOAD_USER", { jwt: hgsJwt });
        routerHistory.replace("/");
      }
    } else {
      setSSOLogin(false);
      document.body.classList.add("login-page");
      return () => document.body.classList.remove("login-page");
    }
  }, []);

  if (switchLoginScreen === "forgotPassword") return <ForgotPassword />;
  if (switchLoginScreen === "verificationCode") return <VerificationCode />;
  if (switchLoginScreen === "resetPassword") return <ResetPassword />;
  if (switchLoginScreen === "linkExpired") return <LinkExpired />;
  if (switchLoginScreen === "passwordCreated") return <PasswordCreated />;
  else {
    return isSSOLogin ? <CircularProgress /> : <SignInPage />;
  }
}

function SignInPage() {
  let { loggingIn } = useSelector((store) => store.user);
  let isMobile = useSelector((store) => store.page.isMobile);
  let [state, update] = useUpdateState({});
  let installed = isInstalled();

  // Check if banner should be visible and manage body class
  useEffect(() => {
    const bannerDismissed = localStorage.getItem("hgs-banner-dismissed");
    const shouldShowBanner = isMobile && !installed && !bannerDismissed;

    // Add/remove body class based on banner visibility
    if (shouldShowBanner) {
      document.body.classList.add("has-banner");
    } else {
      document.body.classList.remove("has-banner");
    }

    // Clean up on unmount
    return () => {
      document.body.classList.remove("has-banner");
    };
  }, [isMobile, installed]);

  function updateValue(name, value) {
    update({
      [name]: value,
      errors: {
        ...(state.errors || {}),
        [name]: null,
      },
    });
  }

  function login() {
    if (loggingIn) return;

    let username = (state.username || "").trim();
    let password = (state.password || "").trim();
    let errors = {};

    if (!username) errors.username = "Username is required.";
    if (!password) errors.password = "Password is required.";

    if (Object.keys(errors).length > 0) {
      update({ errors });
      return;
    }

    update({ errors: null });
    actions.user.login(username, password);
  }

  const handleBannerDismiss = () => {
    document.body.classList.remove("has-banner");
  };

  return (
    <>
      <MobileDownloadBanner onDismiss={handleBannerDismiss} />
      <div className="login-shell">
        <div className="login-brand">
          <img src="/lib/images/Vector.svg" alt="High Ground Solutions" />
          <div className="login-brand-caption">High Ground Solutions</div>
        </div>
        <div className="login-screen sign-in-screen">
          <h2 className="sign-in-title">Account Log In</h2>

          <GuiTextbox
            label="Username"
            name="username"
            value={state.username}
            onChange={updateValue}
            error={state.errors?.username}
            required
            dataQa="username"
          />
          <GuiTextbox
            label="Password"
            name="password"
            value={state.password}
            onChange={updateValue}
            error={state.errors?.password}
            dataQa="password"
            className="password"
            type="password"
            required
            onKeyUp={(e) => e.keyCode === 13 && login()}
          />

          <GuiButton onClick={login} disabled={loggingIn} dataQa="login">
            {loggingIn ? <Spinner /> : "Log In"}
          </GuiButton>
          <div
            className="link"
            onClick={() =>
              actions.passwordRecovery.changeScreen("forgotPassword")
            }
            data-qa="forgotPassword"
          >
            Forgot Password
          </div>
          <ToastContainer />
        </div>
      </div>
    </>
  );
}
